import {
    CardCvcElement,
    CardExpiryElement,
    CardNumberElement,
    PaymentRequestButtonElement,
    useElements,
    useStripe
} from '@stripe/react-stripe-js';
import { PaymentMethod, PaymentRequestOptions } from '@stripe/stripe-js';
import type { StripeCardNumberElementChangeEvent } from '@stripe/stripe-js';
import { useFormikContext } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Accordion, AccordionCollapse, AccordionContext, Col, Form, Row, useAccordionToggle } from 'react-bootstrap';
import styled, {keyframes} from 'styled-components';
import { t } from 'ttag';
import config from '../../../../config/channels';
import useBasketContext from '../../../../contexts/basket/useBasketContext';
import {CustomerFormObject} from '../../../../contexts/customer/customer-context';
import useOperatorContext from '../../../../contexts/operator/useOperatorContext';
import useOrderContext from '../../../../contexts/order/useOrderContext';
import useSiteContext from '../../../../contexts/site/useSiteContext';
import { CTA } from '../../../general/Button';
import { FormData } from './FormBuilder';
import cards from '../../../../assets/images/cards.svg';
import cardsNonAmex from '../../../../assets/images/cards-non-amex.svg';
import mastercard from '../../../../assets/images/mastercard.svg';
import visa from '../../../../assets/images/visa.svg';
import amex from '../../../../assets/images/amex.svg';
import apple_pay from '../../../../assets/images/logo-apple-pay.svg';
import google_pay from '../../../../assets/images/logo-google-pay.svg';
import cvc from '../../../../assets/images/cvc.svg';

import TimeslotModal from '../../../timeslots/TimeslotModal';
import Radio from '../../../general/Radio';
import PageSpinnerThree from "../../../general/PageSpinnerThree";
import {StripeElementChangeEvent} from "@stripe/stripe-js/types/stripe-js/elements/base";
import {iLog} from "../../../../index";
import {TError} from "../../../../contexts/order/order-context";
import {validatePostcode} from "../../../../services/api/site";
import {operatorName} from "../../../../config/operator";
import {TSite} from "../../../../contexts/site/site-context";
import {money} from "../../../../services/format";
import PrivacyModal from "./PrivacyModal";
import Zillionaire from "./Zillionaire";
import TermsModal from "./TermsModal";
import RadioGroup from "../../../general/RadioGroup";
import {rgba} from "polished";
import {Theme} from "../../../../contexts/operator/operator-context";
import ChangeAddressModal from "../ChangeAddressModal";

type PaymentFormProps = {
    form: FormData;
};

interface ErrorDictionary {
    invalid_number: string,
    invalid_expiry_month: string,
    invalid_expiry_year:  string,
    invalid_cvc:  string,
    incorrect_number: string,
    incomplete_number:  string,
    incomplete_cvc:  string,
    incomplete_expiry: string,
    expired_card: string,
    incorrect_cvc:  string,
    incorrect_zip:  string,
    invalid_expiry_year_past:  string,
    card_declined:  string,
    missing: string,
    processing_error: string,
}

const PaymentForm = ({ form }: PaymentFormProps) => {
    const stripe = useStripe();
    const elements = useElements();

    const [totalPayment, setTotalPayment] = useState(0);

    const [cardErrors, setCardErrors] = useState({
        cardNumber: '',
        cardExpiry: '',
        cardCvc: '',
    });

    const [paymentType, setPaymentType] = useState<'mobile' | 'card' | 'express'>('mobile');
    const [paymentRequest, setPaymentRequest] = useState<any>(null);
    const [mobilePayMethod, setMobilePayMethod] = useState<any>(null);
    const [mobilePayEnabled, setMobilePayEnabled] = useState<boolean>(false);
    const [fullyDiscountedOrder, setFullyDiscountedOrder] = useState<boolean>(false);
    const [privacyShow, setPrivacyShow] = useState(false);
    const [termsShow, setTermsShow] = useState(false);

    const [timeslotShow, setTimeslotShow] = useState(false);
    const [addressModalShow, setAddressModalShow] = useState(false);

    const operatorContext = useOperatorContext();
    const basketContext = useBasketContext();
    const orderContext = useOrderContext();
    const siteContext = useSiteContext();

    const handleTimeslotClose = () => {
        setTimeslotShow(false);
    };

    const [card, setCard] = useState({
        card: false,
        cvc: false,
        expiry: false,
    });

    const [cardBrand, setCardBrand] = useState<string|null>(null);
    const [disallowedCard, setDisallowedCard] = useState<boolean>(false);
    const [disallowedWalletCard, setDisallowedWalletCard] = useState<boolean>(false);
    const [optInMarketing, setOptInMarketing] = useState<boolean>(operatorContext.operator?.theme.v4.opt_in_default ?? false);

    const [cardComplete, setCardComplete] = useState(false);
    const [validationErrors, setValidationErrors] = useState<TError[]>([]);

    useEffect(() => {
        let valid = card.card && card.cvc && card.expiry && form.firstName && form.lastName && form.email && form.phone;
        let valid_dine_in = card.card && card.cvc && card.expiry && form.firstName && form.lastName && form.email;

        iLog('cardComplete', [valid, valid_dine_in, card, form]);

        if (siteContext.channel === 'dine_in') {
            setCardComplete(valid_dine_in ?? false);
        } else {
            setCardComplete(valid ?? false);
        }
    }, [card, form]);

    const {
        submitForm,
        validateForm,
        isValid,
        isSubmitting,
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
    } = useFormikContext();

    const formikValueRef = useRef(values);
    const formikValidRef = useRef(isValid);
    const paymentInFlight = useRef(false);

    // Single source of truth for disabling the pay button and showing the page overlay
    const paymentInProgress = orderContext.loading || isSubmitting;

	const companyNameInputRef = useRef(null);
    const nameInputRef = useRef(null);
    const lastNameInputRef = useRef(null);
    const emailInputRef = useRef<any>(null);
    const postcodeInputRef = useRef<any>(null);

    let pr = useRef<any>(null);
    const [prHasBeenSet, setPrHasBeenSet] = useState<boolean>(false);

    const CUSTOMER_NOTES_LIMIT = 100;
    const [customerNotes, setCustomerNotes] = useState("");

    useEffect(() => {
        formikValueRef.current = values;
    }, [values]);

    useEffect(() => {
        basketContext.setCustomerEmail(emailInputRef?.current?.value);
    }, [values])

    useEffect(() => {
        iLog('what is invalid?', [formikValueRef.current, formikValidRef.current]);
    }, [isValid]);

    useEffect(() => {
        if (basketContext.validatedBasket?.total === 0) {
            setFullyDiscountedOrder(true);
            setPaymentType('express');
        }
    }, [basketContext]);

    const editPostcode = () => {
        setAddressModalShow(true)
    };

    const errorDictionary:ErrorDictionary = {
        invalid_number: "Enter a valid card number",
        invalid_expiry_month: "Enter a valid expiration month",
        invalid_expiry_year: "Enter a valid expiration year",
        invalid_cvc: "Enter a valid security code",
        incorrect_number: "Enter a correct card number",
        incomplete_number: "Enter a complete card number",
        incomplete_cvc: "Enter a complete security code",
        incomplete_expiry: "Enter a complete expiration date",
        expired_card: "The card has expired",
        incorrect_cvc: "The card’s security code is incorrect",
        incorrect_zip: "The card’s zip code failed validation",
        invalid_expiry_year_past: "The card’s expiration year is in the past",
        card_declined: "The card was declined",
        missing: "There is no card on a customer that is being charged",
        processing_error: "An error occurred while processing the card"
    };

    useEffect(() => {
        if (stripe && basketContext.validatedBasket) {
            let base = basketContext.validatedBasket?.total ?? 0;

            if ( basketContext.validatedBasket.channel === 'dine_in' ) {
                base = orderContext.serviceSelected
                    ? basketContext.validatedBasket?.total_service_selected ?? 0
                    : basketContext.validatedBasket?.total_service_unselected ?? 0
            }

            let val = base + orderContext.gratuityVal;

            if (prHasBeenSet) {
                pr.current.update({
                    total: {
                        label: operatorContext.operator?.name || 'Food',
                        amount: val,
                    },
                });
            }

            setTotalPayment(val);
        }
    }, [stripe, prHasBeenSet, basketContext.validatedBasket, orderContext.gratuityVal, orderContext.serviceSelected]);

    useEffect(() => {
        if (stripe && basketContext.validatedBasket) {
            let prOptions: PaymentRequestOptions = {
                country: siteContext.site?.address_country ?? 'GB',
                currency: 'gbp',
                disableWallets: ['link'],
                total: {
                    label: operatorContext.operator?.name || 'Food',
                    amount: basketContext.validatedBasket?.total,
                },
                requestPayerName: false,
                requestPayerEmail: false,
                requestPayerPhone: false,
            };

            if (siteContext.channel === config.dineIn.name) {
                prOptions.requestPayerPhone = false;
            }

            iLog('Payment request options ', prOptions);

            pr.current = stripe.paymentRequest(prOptions);
            iLog('Payment request ', pr);

            let libraryInUse: string | null = 'your device';

            // Check the availability of the Payment Request API.
            pr.current.canMakePayment().then((result: { [x: string]: any; } | null) => {
                console.log('Mobile payment activated?', result !== null);

                if (result) {
                    const libTextMapping: { [key: string]: string } = {
                        applePay: 'Apple Pay',
                        googlePay: 'Google Pay',
                        link: 'Link Payment',
                    };

                    for (const library in result) {
                        if (result[library]) {
                            libraryInUse = libTextMapping[library];
                            break;
                        }
                    }
                }

                if (result) {
                    console.log("Let's show our mobile payment button!");
                    setMobilePayEnabled(true);
                    setMobilePayMethod(libraryInUse);
                    setPaymentRequest(pr.current);
                    setPrHasBeenSet(true);
                    setMobilePayEnabled(true);
                }

                setPaymentType('card');
            });

            pr.current.on('paymentmethod', async (ev: { payerName: string; payerEmail: any; shippingAddress: { postalCode: string | undefined; addressLine: any[]; city: string | undefined; }; payerPhone: string; paymentMethod: PaymentMethod | undefined; complete: (arg0: string) => void; }) => {
                iLog('paymentmethod event', ev);

                // If the operator has disabled premium card acceptance, show an error message and cancel the payment request
                // This is not the best UX (since premium cards will still appear in the wallet to choose from), but it's the best we can do right now
                if (!operatorContext.operator?.allow_premium_card_acceptance && ev.paymentMethod?.card?.brand == 'amex') {
                    setDisallowedWalletCard(true);
                    ev.complete('fail');
                    return;
                }

                let dataObject = {
                    ...(formikValueRef.current as CustomerFormObject),
                    opt_in_marketing: optInMarketing
                } as CustomerFormObject;

                iLog('Data object', dataObject);

                // Get order data with this payment method
                let orderData = orderContext.getOrderData(dataObject, ev.paymentMethod);

                if (basketContext.validatedBasket?.ready_time) {
                    orderData = { ...orderData, ...{ time: basketContext.validatedBasket?.ready_time?.id } };
                } else if (basketContext.timeslot) {
                    orderData = { ...orderData, ...{ time: basketContext.timeslot.id } };
                }

                // Handle case where timeslot wasn't set somehow
                if (orderData.time === '') {
                    ev.complete('success');
                    setTimeslotShow(true);
                    return;
                }

                // Remember me
                rememberMe(values);

                // Payment method retrieved, proceed with ordering
                try {
                    ev.complete('success');
                    await orderContext.processOrder(orderData, stripe);
                } catch (error: any) {
                    ev.complete('fail');
                    console.error('Device payment failed.', error);

                    if (error.code === 860 || error.code === 861) {
                        // Invalid timeslot
                        setTimeslotShow(true);
                    }
                }
            });
        }
    }, [stripe, basketContext.validatedBasket]);

    const getErrorText = (error: {
        type: 'validation_error';
        code: string;
        message: string;
    } | undefined) => {
        if (error) {
            return errorDictionary[error.code as keyof ErrorDictionary] ? errorDictionary[error.code as keyof ErrorDictionary] : error.message;
        } else {
            return ''
        }
    }

    const rememberMe = (orderData: any) => {
        if ( orderData.remember_me && operatorContext.operator?.theme.v4.remember_me ) {
            localStorage.setItem('user', JSON.stringify({
            	"companyName": orderData.companyName,
                "firstName": orderData.firstName,
                "lastName": orderData.lastName,
                "email": orderData.email,
                "tel": orderData.tel,
                "remember_me": true
            }));
        } else {
            localStorage.removeItem('user');
        }
    }

    const onCardChange = (event: StripeElementChangeEvent, type: 'card' | 'cvc' | 'expiry') => {
        if (type === 'cvc') {
            setCardErrors(currentErrors => ({
                ...currentErrors,
                cardCvc: getErrorText(event.error),
            }));
        } else if (type === 'expiry') {
            setCardErrors(currentErrors => ({
                ...currentErrors,
                cardExpiry: getErrorText(event.error),
            }));
        }

        if (event.complete) {
            validateForm();
            setCard((prev) => ({
                ...prev,
                [type]: true,
            }));

            if (event.elementType === "cardExpiry") {
                elements?.getElement(CardCvcElement)?.focus();
            }
        } else {
            setCard((prev) => ({
                ...prev,
                [type]: false,
            }));
        }
    };

    const onCardChangeCardNumber = (event: StripeCardNumberElementChangeEvent) => {
        setCardErrors(currentErrors => ({
            ...currentErrors,
            cardNumber: getErrorText(event.error),
        }));
        if (event.complete) {
            elements?.getElement(CardExpiryElement)?.focus();
            validateForm();
            setCard((prev) => ({
                ...prev,
                card: true,
            }));
        } else {
            setCard((prev) => ({
                ...prev,
                card: false,
            }));
        }

        if (event.brand === 'visa') {
            setCardBrand(visa);
            setDisallowedCard(false);
        } else if (event.brand === 'amex') {
            // If the operator has disabled premium card acceptance, show an error message and prevent the user from submitting
            setCardBrand(amex);
            setDisallowedCard(!operatorContext.operator?.allow_premium_card_acceptance);
        } else if (event.brand === 'mastercard') {
            setCardBrand(mastercard);
            setDisallowedCard(false);
        } else {
            setCardBrand(null);
            setDisallowedCard(false);
        }
    };

    const handleCardPayment = async () => {
        // State updates are async, so a fast second click could get in before the button is disabled
        if (paymentInFlight.current) {
            return;
        }

        paymentInFlight.current = true;
        orderContext.setLoading(true);

        try {
            await submitCardPayment();
        } finally {
            // Covers every early return and error path, so the button and overlay can't get stuck
            paymentInFlight.current = false;
            orderContext.setLoading(false);
        }
    };

    const submitCardPayment = async () => {
        // Submit customer details
        await submitForm().catch((error) => {
            throw new error();
        });

        iLog('is fullyDiscountedOrder?', [fullyDiscountedOrder, paymentType]);

        if (fullyDiscountedOrder) {
            return handleNoCardPayment();
        }

        iLog('continue?', [!stripe, !elements, !orderContext.orderData, !isValid]);

        if (!stripe || !elements || !orderContext.orderData || !isValid) {
            validateForm().then((r) => {
                if ( Object.keys(r).length !== 0 ) {
                    const el = document.getElementById(Object.keys(r)[0]);
                    el?.focus();
                    el?.scrollIntoView({block: "start", behavior: "smooth"});
                }
            });

            orderContext.setLoading(false);
            return;
        }

        let card = elements.getElement(CardNumberElement);

        iLog('card?', card);

        if (!card) {
            console.error('No card');
            orderContext.setLoading(false);
            return;
        }

        iLog('paymentType', paymentType);

        if (basketContext.basket && basketContext.basket.total !== 0) {
            // Use your card Element with other Stripe.js APIs
            const { error, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: card,
            });

            if (error) {
                orderContext.setLoading(false);
                return;
            }

            iLog('paymentMethod success', paymentMethod);

            // Get order data with this payment method
            let orderData = orderContext.getOrderData(values as CustomerFormObject, paymentMethod);

            iLog('orderData', orderData);

            // Handle case where timeslot wasn't set somehow
            if (orderData.time === '') {
                setTimeslotShow(true);
                return;
            }

            // Remember me
            rememberMe(values);

            iLog('Processing order with customer', orderData);

            setValidationErrors([]);

            // Payment method retrieved, proceed with ordering
            try {
                await orderContext.processOrder(orderData, stripe);
            } catch (error: any) {
                console.error('Card payment failed.', error);

                if (error.code === 851) { // inline validation errors
                    // validation error comes in as a json string
                    let error_obj = JSON.parse(error.message);

                    iLog('error obj', error_obj);

                    let keys = Object.keys(error_obj);
                    let parsed_obj: TError[] = [];

                    iLog('keys', keys);

                    keys.forEach((key, index) => {
                        iLog('setting error', [key, error_obj[key]]);
                        parsed_obj.push({
                            field: key,
                            messages: error_obj[key]
                        });
                    });
                    setValidationErrors(parsed_obj);
                } else if (error.code === 860 || error.code === 861) {
                    // Invalid timeslot
                    setTimeslotShow(true);
                }
            }
        }
    };

    const handleNoCardPayment = async () => {
        if (!orderContext.orderData) {
            orderContext.setLoading(false);
            return;
        }

        // Get order data with this payment method
        let orderData = orderContext.getOrderData(values as CustomerFormObject);

        if (basketContext.validatedBasket?.ready_time) {
            orderData = { ...orderData, ...{ time: basketContext.validatedBasket?.ready_time?.id } };
        } else if (basketContext.timeslot) {
            orderData = { ...orderData, ...{ time: basketContext.timeslot.id } };
        }

        iLog('Processing order with customer', orderData.customer);

        // Handle case where timeslot wasn't set somehow
        if (orderData.time === '') {
            setTimeslotShow(true);
            return;
        }

        // Payment method retrieved, proceed with ordering
        try {
            await orderContext.processOrder(orderData);
        } catch (error: any) {
            console.error('No card payment failed.', error);

            if (error.code === 860 || error.code === 861) {
                // Invalid timeslot
                setTimeslotShow(true);
            }
        }
    };

    const renderContactFormElements = () => {
        const v: any = values;
        const th: any = touched;
        const e: any = errors;

        return (
            <>
                { !fullyDiscountedOrder && (<FormHeading first={true}>{t`Contact`}</FormHeading>) }
                { form.firstName && (
                    <Form.Group controlId="firstName">
                        <Form.Control
                            type="text"
                            name="firstName"
                            value={v.firstName}
                            autoComplete="given-name"
                            onBlur={handleBlur}
                            ref={nameInputRef}
                            onChange={handleChange}
                            isValid={th.firstName && !e.firstName}
                            isInvalid={th.firstName && !!e.firstName}
                            placeholder={t`First name`}
                            disabled={orderContext.loading}
                            style={{ scrollMarginTop: '74px' }}
                        />
                        <Form.Control.Feedback type="invalid">{e.firstName}</Form.Control.Feedback>
                        <FeedbackElement
                            className={`invalid-feedback ${validationErrors?.filter((m) => m.field === 'first_name') ? 'show-important' : ''}`}>
                            {validationErrors?.filter((m) => m.field === 'first_name').map((m) => m.messages)}
                        </FeedbackElement>
                    </Form.Group>
                )}

                { form.lastName && (
                    <Form.Group controlId="lastName">
                        <Form.Control
                            type="text"
                            name="lastName"
                            value={v.lastName}
                            autoComplete="family-name"
                            onBlur={handleBlur}
                            ref={lastNameInputRef}
                            onChange={handleChange}
                            isValid={th.lastName && !e.lastName}
                            isInvalid={th.lastName && !!e.lastName}
                            placeholder={t`Last name`}
                            disabled={orderContext.loading}
                            style={{ scrollMarginTop: '74px' }}
                        />

                        <Form.Control.Feedback type="invalid">{e.lastName}</Form.Control.Feedback>
                        <FeedbackElement
                            className={`invalid-feedback ${validationErrors?.filter((m) => m.field === 'last_name') ? 'show-important' : ''}`}>
                            {validationErrors?.filter((m) => m.field === 'last_name').map((m) => m.messages)}
                        </FeedbackElement>
                    </Form.Group>
                )}

                { form.email && (
                    <Form.Group controlId="email">
                        <Form.Control
                            type="email"
                            name="email"
                            value={v.email}
                            autoComplete="email"
                            ref={emailInputRef}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            isValid={th.email && !e.email}
                            isInvalid={th.email && !!e.email}
                            placeholder={t`Email`}
                            disabled={orderContext.loading}
                            style={{ scrollMarginTop: '74px' }}
                        />

                        <Form.Control.Feedback type="invalid">{e.email}</Form.Control.Feedback>
                        <FeedbackElement
                            className={`invalid-feedback ${validationErrors?.filter((m) => m.field === 'email') ? 'show-important' : ''}`}>
                            {validationErrors?.filter((m) => m.field === 'email').map((m) => m.messages)}
                        </FeedbackElement>
                    </Form.Group>
                )}

                { form.phone && (
                    <Form.Group controlId="tel" className='phonenum'>
                        <Form.Control
                            type="tel"
                            name="tel"
                            value={v.tel}
                            autoComplete="tel"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            isValid={th.tel && !e.tel}
                            isInvalid={th.tel && !!e.tel}
                            placeholder={t`Phone (mobile only)`}
                            style={ fullyDiscountedOrder ? { marginBottom: '24px', scrollMarginTop: '74px' } : { scrollMarginTop: '74px' } }
                            disabled={orderContext.loading}
                        />
                        <Form.Control.Feedback type="invalid">{e.tel}</Form.Control.Feedback>
                        <FeedbackElement
                            className={`invalid-feedback ${validationErrors?.filter((m) => m.field === 'tel') ? 'show-important' : ''}`}>
                            {validationErrors?.filter((m) => m.field === 'tel').map((m) => m.messages)}
                        </FeedbackElement>
                    </Form.Group>
                )}

                { (operatorContext.operator?.theme.v4.remember_me || (form.opt_in_marketing && operatorContext.operator?.theme.v4.opt_in)) && (
                    <StyledOptInWrapper>
                        { operatorContext.operator?.theme.v4.remember_me && (
                            <StyledOptIn>
                                <Form.Group controlId="remember_me" className={'opt_in_group'}>
                                    <Form.Control
                                        type="checkbox"
                                        name="remember_me"
                                        checked={v.remember_me}
                                        onBlur={handleBlur}
                                        onChange={(e) => setFieldValue('remember_me', (e.target as HTMLInputElement).checked)}
                                        className={'checkbox'}
                                    />
                                    <svg
                                        className={'text-white'}
                                        width={14}
                                        height={14}
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M2.293 7.707l3.774 3.774 5.702-6.841-1.537-1.28-4.299 5.159-2.226-2.226-1.414 1.414z"
                                            fill="currentColor"/>
                                    </svg>
                                    <OptInLabel htmlFor={'remember_me'}>
                                        Remember me for faster checkout next time
                                    </OptInLabel>
                                </Form.Group>
                            </StyledOptIn>
                        )}

                        { form.opt_in_marketing && operatorContext.operator?.theme.v4.opt_in && (
                            <StyledOptIn>
                                <Form.Group controlId="opt_in_marketing" className={'opt_in_group'}>
                                    <Form.Control
                                        type="checkbox"
                                        name="opt_in_marketing"
                                        checked={v.opt_in_marketing}
                                        onBlur={handleBlur}
                                        onChange={(e) => setFieldValue('opt_in_marketing', (e.target as HTMLInputElement).checked)}
                                        className={'checkbox'}
                                    />
                                    <svg
                                        className={'text-white'}
                                        width={14}
                                        height={14}
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M2.293 7.707l3.774 3.774 5.702-6.841-1.537-1.28-4.299 5.159-2.226-2.226-1.414 1.414z"
                                            fill="currentColor"/>
                                    </svg>
                                    <OptInLabel htmlFor={'opt_in_marketing'}>
                                        { operatorContext.operator?.theme.v4.opt_in }
                                    </OptInLabel>
                                </Form.Group>
                            </StyledOptIn>
                        )}
                    </StyledOptInWrapper>
                )}

                {form.address ? (
                    <div className={ siteContext.channel === 'venues' ? 'd-none' : ''}>
                        <FormHeading>{t`Delivery`}</FormHeading>

                        { form.companyName && (
                            <Form.Group controlId="companyName">
                                <Form.Control
                                    type="text"
                                    name="companyName"
                                    value={v.companyName}
                                    autoComplete="organization"
                                    onBlur={handleBlur}
                                    ref={companyNameInputRef}
                                    onChange={handleChange}
                                    placeholder={t`Company name`}
                                    disabled={orderContext.loading}
                                    style={{ scrollMarginTop: '74px' }}
                                />
                                <Form.Control.Feedback type="invalid">{e.companyName}</Form.Control.Feedback>
                                <FeedbackElement
                                    className={`invalid-feedback ${validationErrors?.filter((m) => m.field === 'company_name') ? 'show-important' : ''}`}>
                                    {validationErrors?.filter((m) => m.field === 'company_name').map((m) => m.messages)}
                                </FeedbackElement>
                            </Form.Group>
                        )}

                        <Form.Group controlId="address1">
                            <Form.Control
                                type="address"
                                name="address1"
                                value={v.address1}
                                placeholder={'Address line 1'}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                isValid={th.address1 && !e.address1}
                                isInvalid={th.address1 && !!e.address1}
                                disabled={orderContext.loading || siteContext.channel == 'venues'}
                            />
                            <Form.Control.Feedback type="invalid">
                                <div>
                                    <i className='bx bxs-info-circle' />{e.address1}
                                </div>
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group controlId="address2">
                            <Form.Control
                                type="address"
                                name="address2"
                                value={v.address2}
                                placeholder={'Address line 2'}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                isValid={th.address2 && !e.address2}
                                isInvalid={th.address2 && !!e.address2}
                                disabled={orderContext.loading || siteContext.channel == 'venues'}
                            />
                            <Form.Control.Feedback type="invalid">
                                <div>
                                    <i className='bx bxs-info-circle' />{e.address2}
                                </div>
                            </Form.Control.Feedback>
                        </Form.Group>
                        <StyledRow blockMobile>
                            <Form.Group controlId="city">
                                <Form.Control
                                    type="city"
                                    name="city"
                                    value={v.city}
                                    placeholder={"Town/City"}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    isValid={th.city && !e.city}
                                    isInvalid={th.city && !!e.city}
                                    disabled={orderContext.loading || siteContext.channel == 'venues'}
                                />
                                <Form.Control.Feedback type="invalid">
                                    <div>
                                        <i className='bx bxs-info-circle' />{e.city}
                                    </div>
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group controlId="postcode">
                                <Form.Control
                                    type="postcode"
                                    name="postcode"
                                    placeholder={"Postcode"}
                                    ref={postcodeInputRef}
                                    value={v.postcode}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    isValid={th.postcode && !e.postcode}
                                    isInvalid={th.postcode && !!e.postcode}
                                    disabled={orderContext.loading || ['delivery', 'catering', 'venues'].includes(siteContext.channel)}
                                />
                            </Form.Group>
                        </StyledRow>

                        <div>
                            <ChangePostcode onClick={editPostcode}>Want to change your postcode?</ChangePostcode>
                        </div>

                        <StyledAccordion defaultActiveKey="-1">
                            <CustomToggle eventKey="0">
                                <div className="toggle">
                                    <span className="toggle__title">Add delivery instructions</span>
                                </div>
                            </CustomToggle>
                            <AccordionCollapse eventKey="0" timeout={2000}>
                                <>
                                    <OrderNote
                                        placeholder="Add instructions for delivery (optional)"
                                        value={customerNotes}
                                        maxLength={CUSTOMER_NOTES_LIMIT}
                                        className={'form-control--textarea'}
                                        onChange={(e) => { setCustomerNotes(e.target.value); orderContext.setDeliveryInstructions(e.target.value) } }
                                    />
                                    <div className="customer-notes-counter">{ customerNotes.length } / { CUSTOMER_NOTES_LIMIT } characters</div>
                                </>
                            </AccordionCollapse>
                        </StyledAccordion>
                    </div>
                ) : null}
            </>
        );
    };

    const options = {
        style: {
            base: {
                fontSize: '16px',
                color: '#000000',
                fontFamily: `OSSecondaryFont, DM Sans, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif`,
                fontSmoothing: 'antialiased',
                fontWeight: '400',
                lineHeight: '46px',
                '::placeholder': {
                    color: '#8E8E93',
                    fontWeight: '400',
                    lineHeight: '22px'
                },
            },
        },
    };

    const renderMobilePayIcon = () => {
        switch (mobilePayMethod) {
            case 'Google Pay':
                return (<img className={'payment-radio__logo'} src={google_pay}/>)
                //return (<img className={'payment-radio__cards'} src={operatorContext.operator?.allow_premium_card_acceptance ? cards : cardsNonAmex}/>)
            case 'Apple Pay':
                return (<img className={'payment-radio__logo'} src={apple_pay}/>)
                //return (<img className={'payment-radio__cards'} src={operatorContext.operator?.allow_premium_card_acceptance ? cards : cardsNonAmex}/>)
            case 'Link':
                return (<span>Link</span>)
            default:
                return <span>N/A</span>
        }
    }

    const renderPaymentTypes = (paymentType: "mobile" | "card" | "express") => {
        return (
           <PaymentTypes>
               {paymentRequest ? (
                   <div className={"mobileCheckoutWrapper payment-type" + (paymentType === 'mobile' ? ' payment-type--show' : '')}>
                       <PaymentRequestButtonElement
                           className={`mobile-pay-button`}
                           id={'AP-btn'}
                           onClick={(event) => {
                               event.preventDefault();
                               setDisallowedWalletCard(false);
                               validateForm().then((r) => {
                                   if ( Object.keys(r).length === 0 ) {
                                       paymentRequest.show();
                                   } else {
                                       const el = document.getElementById(Object.keys(r)[0]);
                                       el?.focus();
                                       el?.scrollIntoView({block: "start", behavior: "smooth"});
                                   }
                               });
                               // needed to display the errors (for some reason)
                               submitForm();
                           }}
                           options={
                               {
                                   paymentRequest,
                                   style: {
                                       paymentRequestButton: {
                                           //type: 'default',
                                           // One of 'default', 'book', 'buy', or 'donate'
                                           // Defaults to 'default'

                                           //theme: 'dark',
                                           // One of 'dark', 'light', or 'light-outline'
                                           // Defaults to 'dark'
                                           height: '48px',
                                           // Defaults to '40px'. The width is always '100%'.
                                       },
                                   }
                               }
                           }
                       />
                       {disallowedWalletCard && (
                           <ErrorContainer>
                               American Express cards are not accepted. Please use another card.
                           </ErrorContainer>
                       )}
                   </div>
               ) : null}

               <form className={'payment-type' + (paymentType === 'card' ? ' payment-type--show' : '')}>
                   <Form.Group controlId="card">
                       <CardNumberWrapper>
                           <CardNumberElement
                               className={`form-control ${orderContext.loading ? 'disabled-bg': ''}`}
                               onChange={(e: StripeCardNumberElementChangeEvent) => onCardChangeCardNumber(e)}
                               options={{...options, placeholder: '1234 1234 1234 1234', disabled: orderContext.loading }}
                           />
                           {cardBrand && (<img src={cardBrand} alt="" />) }
                           <span className="material-symbols-outlined">lock</span>
                       </CardNumberWrapper>
                       {disallowedCard && (
                           <ErrorContainer>
                               American Express cards are not accepted. Please use another card.
                           </ErrorContainer>
                       )}
                       {cardErrors.cardNumber && (
                           <ErrorContainer>
                               {cardErrors.cardNumber}
                           </ErrorContainer>
                       )}
                   </Form.Group>
                   <StyledRow blockMobile>
                       <Form.Group controlId="expiry">
                           <CardExpiryElement
                               className={`form-control ${orderContext.loading ? 'disabled-bg': ''}`}
                               onChange={(e: StripeElementChangeEvent) => onCardChange(e, 'expiry')}
                               options={{...options, placeholder: 'MM/YY', disabled: orderContext.loading }}
                           />
                           {cardErrors.cardExpiry && (
                               <ErrorContainer>
                                   {cardErrors.cardExpiry}
                               </ErrorContainer>
                           )}
                       </Form.Group>

                       <Form.Group controlId="cvc">
                           <CvcWrapper>
                               <CardCvcElement
                                   className={`form-control ${orderContext.loading ? 'disabled-bg': ''}`}
                                   onChange={(e: StripeElementChangeEvent) => onCardChange(e, 'cvc')}
                                   options={{...options, placeholder: 'CVC', disabled: orderContext.loading }}
                               />

                               {cardErrors.cardCvc && (
                                   <ErrorContainer>
                                       {cardErrors.cardCvc}
                                   </ErrorContainer>
                               )}
                           </CvcWrapper>
                       </Form.Group>
                   </StyledRow>
               </form>

               <CTA
                   disabled={ disallowedCard || paymentInProgress }
                   loading={isSubmitting}
                   // disabled={ !cardComplete || orderContext.loading || !isValid || isSubmitting }
                   onClick={handleCardPayment}
                   block
                   className={`justify-content-center payBtn payment-type ${paymentType === 'card' ? 'payment-type--show' : ''} ${!cardComplete ? 'check-card-incomplete' : ''} ${!cardComplete ? 'check-card-incomplete' : ''} ${orderContext.loading ? 'order-loading' : ''} ${!isValid ? 'is-invalid' : ''} ${isSubmitting ? 'is-submitting' : ''}`}
               >
                   {orderContext.loading && (
                       <StyledSpinnerOverlay show={true}>
                           <PageSpinnerThree show={true}/>
                       </StyledSpinnerOverlay>
                   )}

                   {!orderContext.loading && (
                       <span>{t`Pay`} {money(totalPayment, operatorContext.operator?.ccy, operatorContext.operator?.locale)}</span>
                   )}
               </CTA>
           </PaymentTypes>
        )
    }

    function CustomToggle({ children, eventKey, callback }: any) {
        const currentEventKey = useContext(AccordionContext);

        const isCurrentEventKey = currentEventKey === eventKey;

        const decoratedOnClick = useAccordionToggle(eventKey, () => {
            callback && callback(eventKey);
        });

        return (
            <button
                type="button"
                className={`your-order-toggle customToggle ${isCurrentEventKey ? 'show' : ''}`}
                onClick={decoratedOnClick}
            >
                {children}
                <span className="material-symbols-outlined">keyboard_arrow_down</span>
            </button>
        );
    }

    return (
        <StyledContainer>
            {paymentInProgress && (
                // @types/react-dom@17 ships its own @types/react@17, so its ReactPortal isn't assignable to our ReactNode
                createPortal(
                    <PaymentOverlay role="status" aria-live="polite" aria-label={t`Processing payment`}>
                        <span className="material-symbols-outlined">progress_activity</span>
                    </PaymentOverlay>,
                    document.body
                ) as unknown as React.ReactNode
            )}

            <TermsModal
                show={termsShow}
                onHide={() => { setTermsShow(false); }}
            />

            <PrivacyModal
                show={privacyShow}
                onHide={() => { setPrivacyShow(false); }}
            />

            <TimeslotModal show={timeslotShow} onHide={handleTimeslotClose} />

            {form.address ? <ChangeAddressModal show={addressModalShow} onHide={() => {setAddressModalShow(false)}}/> : null}

            {!fullyDiscountedOrder ? (
                <>
                    <div className={`cardCheckoutWrapper ${paymentType !== 'card' ? '' : ''} ${orderContext.loading ? 'form--submitting' : ''}`}>
                        <FormCard border={true} topPadding={true}>
                            {renderContactFormElements()}
                        </FormCard>

                        <Zillionaire page={'payment'} />

                        <FormCard>
                            { !fullyDiscountedOrder && (<FormHeading>{t`Payment`}</FormHeading>) }
                            <div className={'payment-radio'}>
                                {mobilePayEnabled && mobilePayMethod ? (<RadioGroup selected={paymentType === "mobile"} onChange={() => { setPaymentType('mobile'); setDisallowedWalletCard(false) }} label={mobilePayMethod} icon={renderMobilePayIcon()} separate={true}/>) : null}
                                <RadioGroup selected={paymentType === "card"} onChange={() => { setPaymentType('card'); setDisallowedWalletCard(false) }} label={'Card'} icon={<img className={'payment-radio__cards'} src={operatorContext.operator?.allow_premium_card_acceptance ? cards : cardsNonAmex}/>} separate={true}/>
                            </div>
                            {renderPaymentTypes(paymentType)}
                            <div className="privacy-notice">
                                By placing an order, you agree to be bound by our <span onClick={() => setTermsShow(true)}>Terms of Service</span> and our <span onClick={() => setPrivacyShow(true)}>Privacy Policy</span>.
                            </div>
                        </FormCard>
                    </div>
                </>
            ) : null}
            {fullyDiscountedOrder ? (
                <>
                    <FormCard topPadding={true}>
                        <h2 style={{ marginBottom: '24px' }}>{t`Contact`}</h2>
                        {renderContactFormElements()}
                        <CTA
                            disabled={ paymentInProgress || !isValid }
                            loading={ paymentInProgress }
                            onClick={handleCardPayment}
                            block
                        >
                            <PageSpinnerThree show={orderContext.loading} text={t`Place order`}/>
                        </CTA>

                        <div className="privacy-notice">
                            By placing an order, you agree to be bound by our <span onClick={() => setTermsShow(true)}>Terms of Service</span> and our <span onClick={() => setPrivacyShow(true)}>Privacy Policy</span>.
                        </div>
                    </FormCard>
                </>
            ) : null}

        </StyledContainer>
    );
};

const spin = keyframes`
    to { transform: rotate(360deg); }
`;

// Sits above the page and modals, but below Stripe's 3DS challenge frame
const PaymentOverlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(128, 128, 128, 0.5);

    .material-symbols-outlined {
        font-size: 48px;
        line-height: 48px;
        color: var(--text-black);
        animation: ${spin} 1s linear infinite;
    }
`;

const OrderNote = styled.textarea`
    border: 1px solid var(--layouts_container_border);
    font-size: 16px;
    line-height: 24px;
    border-radius: var(--radius_xl);
    padding: 17px;
    width: 100%;
    height: 96px;
    resize: none;

    &::placeholder {
        color: var(--text-grey);
        font-size: 16px;
    }
`;

const FormCard = styled.div<{border?: boolean, topPadding?: boolean}>`
    margin-bottom: 16px;
    ${(props) => props.topPadding ? `
        padding: 0 16px;

        @media screen and (min-width: 993px) {
            padding: 16px 16px 0 16px;
        }
    ` : 'padding: 0 16px;'
    }
    ${(props) => props.border ? 'border-bottom: 1px solid var(--layouts_container_border);' : ''}
`;

const PaymentTypes = styled.div`
    .payment-type {
        display: none;
        &--show {
            display: block;
        }
    }
`;

const StyledRow = styled.div<{blockMobile?: boolean}>`
    display: flex;
    gap: 16px;
    width: 100%;
    .form-group {
        width: 100%;
    }
    ${(props) => props.blockMobile ? `@media (max-width: 392px) { display: block }` : ''}
`;

const FeedbackElement = styled.div`
    &.show-important { display:block !important; }
`;

const StyledSpinnerOverlay = styled.div<{show: boolean}>`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: #EBEBEB;
    transition: opacity 0.1s ease-in-out;
    opacity: ${(props) => props.show ? '1' : '0'};
    border-radius: var(--radius_l);
`;

const FormHeading = styled.h2<{first?: boolean}>`
    margin-bottom: 16px;
    font-family: var(--text_body_font);
    letter-spacing: var(--text_body_font_spacing);
    text-transform: var(--text_body_font_case);
    ${(props) => props.first ? `@media screen and (min-width: 993px) {padding-top: 0}` : ''}
`;

const OptInLabel = styled.label`
    cursor: pointer;
    line-height: 20px;
    input {
        display: none;
    }
    input:checked + label:before {
        background-color: blue;
    }
    i {
        margin-bottom: 4px;
    }
`;

const StyledOptInWrapper = styled.div`
    margin-bottom: 16px;
    padding: 14px 14px 0 14px;
    border: 1px solid var(--layouts_container_border);
    border-radius: var(--radius_xl);
`;

const StyledOptIn = styled.div`
    .opt_in_group {
        display: flex;
        gap: 12px;
        margin-bottom: 14px;
        > span {
            flex-grow: 1;
            font-size: 14px;
            line-height: 18px;
        }
        > div, > span {
            cursor: pointer;
        }
        label {
            color: var(--text-grey);
            margin-bottom: 0;
            user-select: none;
            font-size: 14px;
            line-height: 20px;
        }
        width: 100%;
        position: relative;
        transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out;

        svg {
            pointer-events: none;
            position: absolute;
            top: 2px;
            left: 2px;
        }
    }

    .checkbox {
        padding: 0;
        height: 18px;
        width: 18px;
        flex-shrink: 0;
        margin: 0;
        border-radius: var(--radius_s);
        border: 2px solid var(--border-input) !important;
        -moz-appearance: none;
        -webkit-appearance: none;
        -o-appearance: none;
        transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out;
        cursor: pointer;

        &:checked {
            /* !important so the global .form-control:focus background doesn't hide the checked state while focused (Chrome focuses on click) */
            background-color: ${(props) => props.theme.v4.ui_5} !important;
            border:none !important;
        }

        &:focus:not([disabled]) {
          border-color: transparent !important;
          box-shadow: none !important;
        }

        &:focus:not(:checked) {
            border-color: var(--border-input) !important;
        }

        input {
            position: absolute;
            opacity: 0;
            cursor: pointer;
            height: 0;
            width: 0;
        }
    }
`;

const CardNumberWrapper = styled.div`
    position: relative;

    img {
        position: absolute;
        right: 48px;
        top: 12px;
        max-height: 24px;
        height: 24px;
    }

    span {
        position: absolute;
        right: 12px;
        top: 12px;
        color: var(--text-grey);
        font-size: 24px;
        line-height: 24px;
    }
`;

const CvcWrapper = styled.div`
`;

const StyledContainer = styled.div`

    // temporary fix

    .fake_mobile_payment {
    position:relative;
    height: 48px;
    width: 100%;
        button {
            border-radius: var(--radius_m);
            opacity: 1;
            background-color: black;
            .GooglePayButton {
                border: 0;
                cursor: pointer;
                max-height: 64px;
                min-height: 32px;
                position: relative;
                &-logo {
                    background-origin: content-box;
                    background-position: 50%;
                    background-repeat: no-repeat;
                    background-size: contain;
                    bottom: 0;
                    left: 0;
                    padding: 12px;
                    position: absolute;
                    right: 0;
                    top: 0;
                    background-image: url("https://js.stripe.com/v3/fingerprinted/img/dark-8191afec51483e108a2dc5f17fb0efd0.svg");
                }
            }
            height: 100%;
            left: 0;
            position: absolute;
            top: 0;
            width: 100%;
            z-index: 1;
        }
    }

    border-radius: var(--radius_xl);
    margin-bottom: 0px;

    padding: 0px;
    @media screen and (min-width: 993px) {
        padding: 0;
    }

    .checkout__title {
        vertical-align: middle;
        margin-bottom: 0;
    }

    .payBtn {
        margin-top: 8px;
        padding: 10px 24px;
        height: 48px;

        &:disabled {
            background-color: var(--bg-disabled);
        }
    }

    .cardCheckoutWrapper {
        &.disabled {
            position: relative;

            ::after {
                content: '';
                position: absolute;
                inset: 0;
                background-color: white;
                opacity: 0.6;
            }

            .form-control,
            button {
                pointer-events: none;
                position: relative;
            }
        }
    }

    .mobileCheckoutWrapper {
        margin-top: 24px;

        .separator.belowQuickCheckout {
                margin: 24px 0 24px 0;

                @media screen and (max-width: 767px) {
                    margin: 24px 0 22px 0;
                }
            }

        .mobile-pay-button {
            &.disabled {
                position: relative;
                /* background: gray !important; */
                ::after {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background-color: white;
                    opacity: 0.5;
                }

                input {
                    pointer-events: none;
                }
            }
        }
    }

    .checkoutTypeWrapper {
        margin-top: 0;
        margin-bottom: 24px;
        display: flex;
        align-items: center;
        justify-content: space-between;


        .checkoutType {
            background: url(${visa}) -40px top no-repeat;

            &::checked + label span {
                background: url(${visa}) -40px top no-repeat;
            }
        }
        .radio {
            cursor: pointer;
            margin-right: 8px;
            position: relative;
            /* top: 2px; */
        }

        span {
            font-size: 14px;
            font-weight: 500;
            margin-left: 0px;

            @media screen and (max-width: 767px) {
                position: relative;
                top: 1px;
            }
        }
    }

    .privacy-notice {
        font-size: 12px;
        line-height: 16px;
        font-weight: 400;
        text-align: center;
        color: var(--text-grey);
        padding-top: 16px;
        span {
            color: var(--text-grey);
            text-decoration: underline;
            font-weight: 500;
            &:hover {
                cursor: pointer;
            }
        }
        img.cardsImg {
            @media screen and (max-width: 767px) {
                position: relative;
                top: 2px;
            }
        }
        @media screen and (max-width: 767px) {
            padding-bottom: 16px;
        }
    }

    .payment-radio {
        margin-bottom: 20px;
        &__logo {
            height: 24px;
        }
    }

    .venue-address {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-bottom: 16px;
        color: var(--text-black);
        font-size: 16px;
        line-height: 20px;
    }
`;

const ChangePostcode = styled.div`
    text-align: right;
    font-size: 14px;
    line-height: 10px;
    position: relative;
    top: -8px;
    color: var(--text-grey);
    &:hover {
        cursor: pointer;
    }
`;

const StyledAccordion = styled(Accordion)`
    margin: 16px 0;

    .customToggle {
        transition: background-color 0.15s ease-in-out;

        .material-symbols-outlined {
            transition: transform 0.15s ease-in-out;
            font-size: 24px;
            line-height: 24px;
        }

        &.show {
            .material-symbols-outlined {
                transform: rotate(180deg);
            }
        }
    }

    > button {
        width: 100%;
        background: transparent;
        border: none;
        outline: none;
        box-shadow: none;
        display: flex;
        padding: 0;
        margin: 0;
        margin-bottom: 8px;
        font-weight: 600;
        align-items: center;
        text-align: left;
        color: var(--text_body_color);

        img {
            transition: transform 0.2s linear;
        }

        &.show {
            img {
                transform: rotate(180deg);
            }
        }

        .toggle {
            display: flex;
            color: var(--text_body_color);
            flex-wrap: wrap;
            font-size: 16px;
            font-weight: 500;
            line-height: 24px;

            > span {
                margin-right: 8px;
            }
        }
    }

    .customer-notes-counter {
        padding-top: 4px;
        text-align: right;
        font-size: 14px;
        line-height: 20px;
        color: var(--text-grey);
    }
`;

const ErrorContainer = styled.div`
    color: var(--invalid);
    padding-top: 8px;
    display: flex;
    align-items: flex-start;
    img { margin-right: 12px; }

    & i { margin-right: 8px; }
`;

export default PaymentForm;
